import { FC, useEffect, useState, useMemo } from "react"
import { useStore } from "stores/utils/use-store"

import { TablePaginationConfig } from "antd/lib/table/interface"
import { Alert, Button, Modal, Table } from "antd"
import { observer } from "mobx-react"

import { AddKeyComponent } from "./add-key-form"
import { AddKeysComponent } from "./add-keys.form"
import { UpdateDeleteKey } from "./update-key"

import { GiftCard } from "stores/primitives/gift-card"
import { formatWithMs } from "utils/date"
import { getDuplicateGiftCodes } from "domains/marketplace/utils/gift-card-duplicates"

export const KeysList: FC<{ itemTypeId: string }> = observer(({ itemTypeId }) => {
  const { dataStore: { giftCardsStore } } = useStore()

  const paginationOptions: TablePaginationConfig = {
    onChange: async(page, pageSize) => {
      giftCardsStore.pagination = {
        page,
        limit: pageSize || giftCardsStore.pagination.limit,
      }
    },
    total: giftCardsStore.total,
    showTotal: (total, range) => `${range[0]} to ${range[1]} of ${total}`,
    current: giftCardsStore.pagination.page,
    pageSize: giftCardsStore.pagination.limit,
    showSizeChanger: true,
    pageSizeOptions: ["10", "25", "50", "100", "250", "500", "1000"],
  }

  useEffect(() => {
    giftCardsStore.getData()
  }, [giftCardsStore, itemTypeId])

  const columns: Record<string, any>[] = [
    {
      title: "Created at",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (createdAt: Date) => formatWithMs(createdAt),
    },
    {
      title: "Updated at",
      dataIndex: "updatedAt",
      key: "updatedAt",
      render: (updatedAt: Date) => formatWithMs(updatedAt),
    },
    {
      title: "Login",
      dataIndex: "login",
      key: "login",
      render: (el: string) => el || "Not taken",
    },
    {
      title: "Name",
      dataIndex: ["itemType", "name"],
      key: "name",
    },
    {
      title: "Price",
      dataIndex: ["itemType", "price"],
      key: "price",
    },
    {
      title: "Key",
      dataIndex: "giftCode",
      key: "giftCode",
      render: (key: string) => <span style={{ wordBreak: "break-all", whiteSpace: "normal" }}>{key}</span>,
    },
  ]

  const { duplicateKeys, duplicateCount } = useMemo(
    () => getDuplicateGiftCodes(giftCardsStore.giftCards || []),
    [giftCardsStore.giftCards],
  )

  const [visibleSingleKeyModal, setSingleKeyModalVisibility] = useState<boolean>(false)
  const [visibleMultipleKeyModal, setMultipleKeyModalVisibility] = useState<boolean>(false)
  const [visibleUpdateDeleteKeyModal, setUpdateDeleteKeyModalVisibility] = useState<boolean>(false)

  return (
    <>
      <Button onClick={() => setSingleKeyModalVisibility(true)}>Add new single Key</Button>
      <Button onClick={() => setMultipleKeyModalVisibility(true)}>Add new multiple Keys</Button>
      {duplicateCount > 0 && (
        <Alert
          message={`Found ${duplicateCount} row${duplicateCount > 1 ? "s" : ""} with duplicate keys in the table`}
          type="warning"
          showIcon
          style={{ margin: "8px 0" }}
        />
      )}
      <Table
        dataSource={giftCardsStore.giftCards}
        columns={columns as any}
        rowKey="id"
        pagination={paginationOptions}
        loading={giftCardsStore.isLoading}
        onRow={(record: GiftCard) => ({
          onClick: () => {
            giftCardsStore.selectedElement = record
            setUpdateDeleteKeyModalVisibility(true)
          },
          style: duplicateKeys.has(record.giftCode)
            ? { backgroundColor: "#ffcdd2", borderLeft: "4px solid #d32f2f", fontWeight: 500 }
            : undefined,
        })}
      />
      <Modal
        visible={visibleSingleKeyModal}
        title="Add New Key"
        footer={null}
        onCancel={() => setSingleKeyModalVisibility(false)}
      >
        <AddKeyComponent />
      </Modal>
      <Modal
        visible={visibleMultipleKeyModal}
        title="Add New Keys"
        footer={null}
        onCancel={() => setMultipleKeyModalVisibility(false)}
      >
        <AddKeysComponent onClose={() => setMultipleKeyModalVisibility(false)} />
      </Modal>
      {visibleUpdateDeleteKeyModal && (
        <UpdateDeleteKey
          visible={visibleUpdateDeleteKeyModal}
          onClose={() => setUpdateDeleteKeyModalVisibility(false)}
        />
      )}
    </>
  )
})
